/**
 * What the clinic reads when a turn answered WITHOUT part of the context the
 * screen offered (SCRUM-306).
 *
 * Decision 14: the `data-partial-context` frame carries the ref types and a
 * count, and no reason code at all. A turn whose refs were refused for lack of
 * permission and one whose refs were simply unknown reach this module as the
 * same input, so the copy cannot — and must not — tell them apart. Saying "sem
 * permissão" would confirm that the record exists; saying "não encontrado"
 * would deny it. The sentence says neither.
 */
import type { PageContextRefType } from "@/generated/copilot-contracts";

export type PartialContextCopy = {
  title: string;
  body: string;
};

const REF_TYPE_LABELS: Record<PageContextRefType, string> = {
  patient: "paciente",
  lead: "lead",
  scheduling: "agendamento",
  stock_item: "item de estoque",
};

/** The pt-BR label of a ref type, with a neutral fallback for a type a newer
 * runtime may send. */
export function refTypeLabel(type: string): string {
  return REF_TYPE_LABELS[type as PageContextRefType] ?? "registro desta tela";
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;
}

/** "de paciente" / "de lead e agendamento" / "desta tela" when no type is named. */
function dataSubject(labels: readonly string[]): string {
  return labels.length === 0 ? "desta tela" : `de ${joinLabels(labels)}`;
}

/** The notice for a turn that answered with part of the page context missing.
 * One input — the types — because the frame carries nothing else. */
export function partialContextCopyFor(
  refTypes: readonly string[],
): PartialContextCopy {
  const labels = Array.from(new Set(refTypes.map(refTypeLabel)));

  return {
    title: "Respondi sem parte do contexto desta tela",
    body: `Não consegui usar os dados ${dataSubject(labels)} nesta resposta. O que está acima foi respondido sem eles — confira antes de usar, ou peça de novo informando os dados diretamente.`,
  };
}
